import {Component, effect, HostListener, inject, resource, signal, viewChild} from '@angular/core';
import {WjFlexGrid, WjGridModule} from '@mescius/wijmo.angular2.grid';
import {ColumnDeclaration, Wijmo, WjGridUtils} from '../../lib/wjgrid-utils';
import {DataType} from '@mescius/wijmo';
import {WjGridColumnChooser} from '../../lib/wjgrid-columchooser';
import {ActivatedRoute, Router} from '@angular/router';
import {WjFlexGridFilter, WjGridFilterModule} from '@mescius/wijmo.angular2.grid.filter';
import {FlexGrid, FormatItemEventArgs} from '@mescius/wijmo.grid';
import {CellMaker} from '@mescius/wijmo.grid.cellmaker';
import {Alerts} from '../../lib/alerts';
import {Button} from '@fluentui/web-components';
import {WjGridSearchModule} from '@mescius/wijmo.angular2.grid.search';
import {AppFormat} from '../../lib/format';
import {Operator} from '@mescius/wijmo.grid.filter';
import {PortfolioDataContext, SegmentWithPermissions} from '../../data/portfolio-data-context.service';
import {PortfolioSegmentDataContext} from '../../data/portfolio-segment-data-context.service';
import {Location} from '@angular/common';
import {groupBy, sumBy} from 'es-toolkit';
import {PortfolioPeriodEnum, SegmentStatisticStatusEnum} from '../../../api-client-kt/src';
import {WjGridCustomCells} from '../../lib/wjgrid-customcells';

const tplCustomButton = CellMaker.makeButton({
    text: '<b>${item.officeName}</b> Button',
    click: (e, ctx) => alert('Clicked Button ** ' + ctx.item.id + ' **'),
});

const tplCustomLink = CellMaker.makeLink({
    text: '<b>${item.officeName}</b> Button',
    href: 'javascript:void(0);',
});

const memberColumns: ColumnDeclaration[] =[
    {header: "Office", binding: "officeName", dataType: DataType.String},
    {header: "Office Key", binding: "officeKey", dataType: DataType.String, visible: false},
    {header: "LoB", binding: "lobName", dataType: DataType.String},
    {header: "LoB Key", binding: "lobKey", dataType: DataType.String, visible: false},
    {header: "Segment Key", binding: "segmentKey", dataType: DataType.String, visible: false},
    {header: "IsHub", binding: "ishub", dataType: DataType.Boolean, visible: false},
    {header: "Count", binding: "count", dataType: DataType.Number, width: 100},
    {header: "In Period", binding: "countPeriod", dataType: DataType.Number, width: 100},
    {header: "Progress", binding: "completionPct", dataType: DataType.Number, format: 'p0', width: 90, cssClass: 'progress-cell'},
    {header: "Open", binding: "stats.Open", dataType: DataType.Number, format: 'g0', width: 70},
    {header: "Done", binding: "stats.Completed", dataType: DataType.Number, format: 'g0', width: 70},
    {header: "Review", binding: "stats.NeedsReview", dataType: DataType.Number, format: 'g0', width: 75},
    {header: "Status", binding: "status", dataType: DataType.String},
    {header: "Permission", binding: "hasPermission", dataType: DataType.Boolean, visible: false},
    {header: "Policy Rule", binding: "policyRule", dataType: DataType.String, visible: false},
    {header: "Overview", binding: "actionOverview", dataType: DataType.String, align: 'center', width: 90},
    {header: "Accounts", binding: "actionAccounts",  dataType: DataType.String, align: 'center', width: 90},
];

const columnTooltips = {
    officeName: 'Where business is underwritten',
    count: 'Total number of accounts in the portfolio in the whole UWY',
    countPeriod: 'Number of accounts in the portfolio for the current period filter',
    completionPct: 'Percentage of accounts in completed status within the selected period',
    stats: 'Number of accounts by their current status. Hover over the cell to see details',
    status: 'Current overall status of the segment',
    'stats.Open': 'Number of accounts in the Open status within the selected period',
    'stats.Completed': 'Number of accounts in the Completed status within the selected period',
    'stats.NeedsReview': 'Number of accounts in the Needs Review status within the selected period',
    actionOverview: 'Click to view the overview charts for this portfolio segment',
    actionAccounts: 'Click to view the account details for this portfolio segment',
}

/**
 * A component managing the members of a portfolio. It provides functionality for
 * viewing, filtering, and navigating through the portfolio members' data
 * i.e. the list of lob + office combinations.
 *
 * Features:
 * - Real-time updates of the grid with data changes.
 * - Navigation support for opening portfolio segments within the app or in a new tab.
 * - Handling dirty states before navigation to avoid unintentional data loss.
 * - Visual indicators for "current segments" and progress representation for completion percentages.
 */
@Component({
    selector: 'app-portfolio-members',
    imports: [
        WjGridModule,
        WjGridFilterModule,
        WjGridSearchModule
    ],
    templateUrl: './portfolio-members.component.html',
    styleUrl: './portfolio-members.component.scss'
})
export class PortfolioMembersComponent{

    protected readonly Wijmo = Wijmo;

    private readonly grid = viewChild(WjFlexGrid);

    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly location = inject(Location);

    private readonly pfData = inject(PortfolioDataContext);
    private readonly pfSegData = inject(PortfolioSegmentDataContext);

    protected readonly AppFormat = AppFormat;

    protected readonly opensInNewContext = signal(false);

    private static StatsWithZeros = Object.fromEntries(Object.keys(SegmentStatisticStatusEnum).map(status => [status, 0]));


    protected members = resource({
        params: () => this.pfData.portfolioId(),
        loader: ({ params }) => this.pfData.fetchPortfolioSegments(params),
        defaultValue: [],
    });

    constructor() {
        effect(() => this.addStatistics(this.members.value()));
        effect(() => this.adaptCanOpenState(this.pfSegData.isDirty(), this.opensInNewContext()));
    }

    @HostListener('window:keydown', ['$event'])
    @HostListener('window:keyup', ['$event'])
    @HostListener('window:mousemove', ['$event'])
    protected updateCanOpenState(event: MouseEvent | KeyboardEvent) {
        event.stopPropagation();
        this.opensInNewContext.set(event.shiftKey || event.ctrlKey);
    }

    private createButton(iconCls: string) : Button {
        const fluentButtonElement = document.createElement('fluent-button') as Button;
        fluentButtonElement.setAttribute('appearance', 'outline');
        fluentButtonElement.classList.add('small-height');
        fluentButtonElement.innerHTML = `<i class="fat ${iconCls}"></i>`;
        return fluentButtonElement;
    }

    async onGridInitialized(grid: WjFlexGrid, gridFilter: WjFlexGridFilter) {
        WjGridUtils.addColumns(grid, memberColumns);
        WjGridCustomCells.addColumnHeaderTooltips(grid, columnTooltips);
        WjGridColumnChooser.addColumnChooser('portfolio.members', grid);
        grid.formatItem.addHandler(this.membersGridFormatHandler.bind(this));

        const hasPermissionFilter = gridFilter.getColumnFilter('hasPermission');
        hasPermissionFilter.valueFilter.showValues = { true: true};

        const countFilter = gridFilter.getColumnFilter('count');
        countFilter.conditionFilter.condition1.operator = Operator.GT;
        countFilter.conditionFilter.condition1.value = 0;

        gridFilter.apply();
    }

    private addStatistics(members: SegmentWithPermissions[]) {
        const period = this.pfSegData.periodFilter();
        members.forEach(it => this.addMemberStats(it, period));
        WjGridUtils.updateGridItemsSourceAndPreserveSelection(this.grid(), members);
        this.grid()?.refresh(true);
    }

    private addMemberStats(seg: SegmentWithPermissions, period: PortfolioPeriodEnum | undefined | null) {
        const statsInPeriod = seg.statsByStatusAndPeriod.filter(it => period == null || it.period === period);
        const groupedByStatus = groupBy(statsInPeriod, it => it.status);

        const statsByStatus = { ... PortfolioMembersComponent.StatsWithZeros };
        for (const status in groupedByStatus) {
            const s = status as SegmentStatisticStatusEnum;
            statsByStatus[s] = sumBy(groupedByStatus[s], it => it.count);
        }

        const totalCount = sumBy(statsInPeriod, it => it.count);
        const completionPct = totalCount > 0 ? statsByStatus[SegmentStatisticStatusEnum.Completed] / totalCount : 0;

        (seg as any).stats = statsByStatus;
        (seg as any).completionPct = completionPct;
        (seg as any).totalCount = seg.count;
        (seg as any).countPeriod = totalCount;
    }

    private membersGridFormatHandler(s: FlexGrid, e: FormatItemEventArgs) {
        const cell = e.cell;

        const col = s.columns[e.col];
        const row = s.rows[e.row];

        if (e.panel === s.cells) {
            const column = e.getColumn();
            const binding = column.binding;
            const item = s.collectionView.items[e.row];

            if (this.pfSegData.segmentId() === item.id) {
                cell.classList.add('current-segment');
            }

            if (binding?.startsWith('action')) {
                const actionType = binding.substring('action'.length);
                const isOverview = actionType === 'Overview';
                const iconsCls = isOverview ? 'fa-pie-chart' : 'fa-list';
                const navUrl = isOverview ? 'overview' : 'accounts';
                const title = isOverview ? 'Overview Charts' : 'Account Details';
                const cb = this.createButton(iconsCls);
                cb.title = title;
                cb.onclick = (event) => this.goToSegmentUrl(event, item.id, navUrl);
                e.cell.classList.add('action-cell');
                e.cell.innerHTML = '';
                e.cell.appendChild(cb);
            } else if (binding === 'status') {
                e.cell.innerHTML = '<fluent-badge appearance="outline" color="error">' + item.status + '</fluent-badge>';
            } else if (binding === 'completionPct') {
                const value = Math.round(item.completionPct * 100);
                const valueText = `${value}%`;
                e.cell.innerHTML = `<fluent-progress-bar thickness="large" max="100" min="0" value="${value}"></fluent-progress-bar> ${valueText}`;
            }
        }
    }

    async onRowDblClick(event: MouseEvent) {
        const row = this.grid()!.hitTest(event).row;
        if (row > -1) {
            const segment = this.grid()!.rows[row].dataItem as SegmentWithPermissions;
            if (segment.hasPermission) {
                await this.goToSegmentUrl(event, segment.id!, 'accounts');
            }
        }
    }

    private async goToSegmentUrl(event: PointerEvent | MouseEvent, segmentId: string, segmentUrl: string) {
        // Open the portfolio in a new tab if the shift or ctrl key is pressed,
        if (event.shiftKey || event.ctrlKey) {
            const tree = this.router.createUrlTree(['../', 'segment', segmentId, segmentUrl], {relativeTo: this.route});
            const serialized = this.router.serializeUrl(tree);
            const external = window.location.origin + this.location.prepareExternalUrl(serialized);
            window.open(external, '_blank')?.focus();
        } else {
            // otherwise open the portfolio in the current tab if it is not dirty
            if (this.pfSegData.isDirty()) {
                await Alerts.alertPopup({
                    body: 'There are pending changes. Please save or discard your changes before navigating.'
                });
                return;
            } else {
                await this.router.navigate(['../', 'segment', segmentId, segmentUrl], {relativeTo: this.route});
            }
        }
    }

    private adaptCanOpenState(isDirty: boolean, opensInNewContext: boolean) {
        const gridHost = this.grid()?.hostElement as HTMLElement;
        if (gridHost) {
            gridHost.classList.toggle('can-not-open', isDirty && !opensInNewContext);
        }
    }
}
